import React, { useState, useEffect } from "react";
import {
  Table,
  Input,
  Button,
  Modal,
  Form,
  message,
  Space,
  Popconfirm,
  Typography,
  Card,
  Tag,
  Select,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import axios from "axios";

const { Title } = Typography;

const CompanyMaintenance = () => {
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState(null);
  const [searchText, setSearchText] = useState("");

  const API_BASE_URL = "https://techcrew.pk/ulc/";

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    filterCompanies();
  }, [companies, searchText]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}Get_Companies.php`);

      if (data.status && data.status.code === 200) {
        setCompanies(data.data);
      } else {
        message.error(data.status?.message || "Failed to fetch companies");
      }
    } catch (error) {
      console.error("API Error:", error);
      message.error(
        error.response?.data?.message || "Failed to connect to server"
      );
    } finally {
      setLoading(false);
    }
  };

  const filterCompanies = () => {
    let filtered = [...companies];

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((item) =>
        item.company_name.toLowerCase().includes(searchLower)
      );
    }

    setFilteredCompanies(filtered);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const url = editingId
        ? `${API_BASE_URL}Update_Company.php`
        : `${API_BASE_URL}Add_Company.php`;

      const payload = editingId ? { ...values, company_id: editingId } : values;

      const { data } = await axios.post(url, payload);

      if (data.status.code === 200) {
        message.success(data.status.message);
        setModalVisible(false);
        fetchCompanies();
      } else {
        throw new Error(data.status.message);
      }
    } catch (error) {
      console.error("Submission error:", error);
      message.error(error.message || "Error saving company");
    }
  };

  const handleDelete = async (id) => {
    try {
      const { data } = await axios.post(`${API_BASE_URL}Delete_Company.php`, {
        company_id: id,
      });

      if (data.status.code === 200) {
        message.success("Company deactivated");
        fetchCompanies();
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      message.error(error.message || "Deactivation failed");
    }
  };

  const columns = [
    {
      title: "Company Name",
      dataIndex: "company_name",
      key: "name",
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "Active" ? "green" : "red"}>{status}</Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setEditingId(record.company_id);
              form.setFieldsValue({
                company_name: record.company_name,
                status: record.status,
              });
              setModalVisible(true);
            }}
          />
          <Popconfirm
            title="Deactivate this company?"
            onConfirm={() => handleDelete(record.company_id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ paddingLeft: "250px", paddingTop: "60px" }}>
      <Card
        title={<Title level={4}>Company Management</Title>}
        bordered={false}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              setEditingId(null);
              setModalVisible(true);
            }}
          >
            Add Company
          </Button>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder="Search companies..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ width: 300 }}
          />
        </div>

        <Table
          columns={columns}
          dataSource={filteredCompanies}
          rowKey="company_id"
          loading={loading}
          scroll={{ x: true }}
          bordered
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingId ? "Edit Company" : "Add New Company"}
        visible={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="company_name"
            label="Company Name"
            rules={[{ required: true, message: "Please enter company name" }]}
          >
            <Input placeholder="Enter company name" />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            initialValue="Active"
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="Active">Active</Select.Option>
              <Select.Option value="Inactive">Inactive</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CompanyMaintenance;
